import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FolderLock, Users, Building2 } from 'lucide-react'
import type { StructureData, GroupType } from '../types.ts'
import { MatrixTab } from './MatrixTab.tsx'
import { UserGroupMatrix } from './UserGroupMatrix.tsx'
import { DepartmentGroupMatrix } from './DepartmentGroupMatrix.tsx'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui'

export type MatrixMode = 'freigaben' | 'users' | 'departments'

const MODES: Array<{
  key: MatrixMode
  label: string
  icon: typeof FolderLock
  hint: string
}> = [
  { key: 'freigaben',   label: 'Freigaben',         icon: FolderLock, hint: 'Ordner × Gruppen mit NTFS-Rechten' },
  { key: 'users',       label: 'User-Matrix',       icon: Users,      hint: 'User × Gruppen (Abteilungen / Software / Lizenzen / Funktionen)' },
  { key: 'departments', label: 'Abteilungs-Matrix', icon: Building2,  hint: 'Abteilung × Software / Lizenzen / Funktionen — AD-Nesting: AB-Gruppe ist Mitglied der Zielgruppe, User erben automatisch' },
]

const VALID_MODES = new Set<MatrixMode>(MODES.map((m) => m.key))

const USER_MATRIX_TYPES: GroupType[] = ['AB', 'LI', 'FU', 'SW']
const VALID_USER_TYPES = new Set<GroupType>(USER_MATRIX_TYPES)

const DEPT_MATRIX_TYPES: GroupType[] = ['LI', 'FU', 'SW']
const VALID_DEPT_TYPES = new Set<GroupType>(DEPT_MATRIX_TYPES)

const LEGACY_MODE_TO_TYPE: Record<string, GroupType | undefined> = {
  abteilung: 'AB',
  software: 'SW',
  lizenzen: 'LI',
  funktionen: 'FU',
}

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
}

export function MatrixTabWithModes({ data, update }: Props) {
  const [searchParams, setSearchParams] = useSearchParams()

  const { mode, legacyFilterType } = useMemo(() => {
    const raw = searchParams.get('matrixMode')
    if (raw && VALID_MODES.has(raw as MatrixMode)) {
      return { mode: raw as MatrixMode, legacyFilterType: undefined as GroupType | undefined }
    }
    const legacy = raw ? LEGACY_MODE_TO_TYPE[raw] : undefined
    if (legacy) {
      return { mode: 'users' as MatrixMode, legacyFilterType: legacy }
    }
    return { mode: 'freigaben' as MatrixMode, legacyFilterType: undefined as GroupType | undefined }
  }, [searchParams])

  const activeTypes = useMemo<GroupType[]>(() => {
    if (legacyFilterType) return [legacyFilterType]
    const param = searchParams.get('userMatrixTypes')
    if (param === null) return USER_MATRIX_TYPES
    if (param === '') return []
    const parsed = new Set(
      param
        .split(',')
        .map((s) => s.trim() as GroupType)
        .filter((t) => VALID_USER_TYPES.has(t)),
    )
    return USER_MATRIX_TYPES.filter((t) => parsed.has(t))
  }, [searchParams, legacyFilterType])

  const deptActiveTypes = useMemo<GroupType[]>(() => {
    const param = searchParams.get('deptMatrixTypes')
    if (param === null) return DEPT_MATRIX_TYPES
    if (param === '') return []
    const parsed = new Set(
      param
        .split(',')
        .map((s) => s.trim() as GroupType)
        .filter((t) => VALID_DEPT_TYPES.has(t)),
    )
    return DEPT_MATRIX_TYPES.filter((t) => parsed.has(t))
  }, [searchParams])

  function setMode(next: MatrixMode) {
    const params = new URLSearchParams(searchParams)
    if (next === 'freigaben') params.delete('matrixMode')
    else params.set('matrixMode', next)
    setSearchParams(params, { replace: true })
  }

  function setActiveTypes(next: GroupType[]) {
    const ordered = USER_MATRIX_TYPES.filter((t) => next.includes(t))
    const params = new URLSearchParams(searchParams)
    params.set('matrixMode', 'users')
    if (ordered.length === USER_MATRIX_TYPES.length) {
      params.delete('userMatrixTypes')
    } else {
      params.set('userMatrixTypes', ordered.join(','))
    }
    setSearchParams(params, { replace: true })
  }

  function setDeptActiveTypes(next: GroupType[]) {
    const ordered = DEPT_MATRIX_TYPES.filter((t) => next.includes(t))
    const params = new URLSearchParams(searchParams)
    params.set('matrixMode', 'departments')
    if (ordered.length === DEPT_MATRIX_TYPES.length) {
      params.delete('deptMatrixTypes')
    } else {
      params.set('deptMatrixTypes', ordered.join(','))
    }
    setSearchParams(params, { replace: true })
  }

  const active = useMemo(() => MODES.find((m) => m.key === mode) ?? MODES[0], [mode])

  return (
    <div className="space-y-3">
      <Tabs value={mode} onValueChange={(v) => setMode(v as MatrixMode)}>
        <div className="flex items-center gap-3">
          <TabsList className="flex-1">
            {MODES.map(({ key, label, icon: Icon, hint }) => (
              <TabsTrigger key={key} value={key} title={hint}>
                <Icon className="w-4 h-4" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="hidden md:block text-caption text-gray-500 pb-2">{active.hint}</div>
        </div>
      </Tabs>

      {mode === 'freigaben' && <MatrixTab data={data} update={update} />}
      {mode === 'users' && (
        <UserGroupMatrix
          data={data}
          update={update}
          groupTypes={USER_MATRIX_TYPES}
          activeTypes={activeTypes}
          onActiveTypesChange={setActiveTypes}
        />
      )}
      {mode === 'departments' && (
        <DepartmentGroupMatrix
          data={data}
          update={update}
          groupTypes={DEPT_MATRIX_TYPES}
          activeTypes={deptActiveTypes}
          onActiveTypesChange={setDeptActiveTypes}
        />
      )}
    </div>
  )
}
